// ===========================================
// PIXI REGISTRY - SHARED CONSTANTS
// ===========================================
// Centralized magic numbers and configuration values
// used across animation, reconciliation, and resync code.
// ===========================================

import { DEFAULT_WEB_TIMING } from '@busted-blackjack/shared';

// ===========================================
// KITTY POSITIONING
// ===========================================

/** Base radial offset from seat center toward dealer for kitty positioning */
export const KITTY_BASE_RADIAL_OFFSET = -134;

/** Gap between paired kitties (main bet + insurance) - matches SPLIT_KITTY_PILL_PADDING */
export const KITTY_PAIR_GAP = 2;

/** Kitty chip size in pixels */
export const KITTY_CHIP_SIZE = 28;

/** Vertical offset between stacked chips in kitty */
export const KITTY_VERTICAL_OFFSET = 8;

// ===========================================
// CARD POSITIONING
// ===========================================

/** Card dimensions */
export const CARD_WIDTH = 74;
export const CARD_HEIGHT = 105;

/** Horizontal overlap between cards in a hand */
export const CARD_OVERLAP = 18;

// ===========================================
// CHIP SIZES
// ===========================================

/** Chip sizes for different contexts */
export const CHIP_SIZES = {
  rail: 48,         // Rail chip size (player's balance)
  staged: 24,       // Staged chip size (bet reference)
  kittyMedium: 28,  // Kitty chip for current player
  kittySmall: 20,   // Kitty chip for opponents
  dealerPile: 20,   // Dealer tray chip size
} as const;

// ===========================================
// BEAM CONFIGURATION
// ===========================================

/** Default beam rotation when hidden (pointing down) */
export const BEAM_HIDDEN_ROTATION = 180;

/** Per-seat beam rotation angles (visual positions 1-7) */
export const BEAM_ROTATIONS: Record<number, number> = {
  1: 261,      // Far left
  2: 237.5,    // Left
  3: 212.5,    // Inner left
  4: 180,      // Center (bottom)
  5: 147.5,    // Inner right
  6: 122.5,    // Right
  7: 99,       // Far right
};

// ===========================================
// SEAT CONFIGURATION
// ===========================================

/** Default seat configuration when no per-seat config provided */
export const DEFAULT_SEAT_CONFIG = {
  x: 0,
  y: 0,
  rot: 0,
  scale: 100,
} as const;

/** Default chip configuration when no per-seat config provided */
export const DEFAULT_CHIP_CONFIG = {
  radiusOffset: 0,
  x: 0,
  y: 0,
  scale: 100,
} as const;

/** Default insurance chip configuration */
export const DEFAULT_INSURANCE_CHIP_CONFIG = {
  x: 35,
  y: 0,
  scale: 100,
} as const;

// ===========================================
// SCOREBOX CONFIGURATION
// ===========================================

/** Default minimum scorebox width */
export const DEFAULT_SCOREBOX_MIN_WIDTH = 35;

/** Minimum text width for scorebox calculations */
export const SCOREBOX_MIN_TEXT_WIDTH = 12;

// ===========================================
// ANIMATION TIMING
// ===========================================
// Values imported from shared package where applicable
// See: shared/src/animation-timing.ts

/** Standard animation durations in milliseconds */
export const ANIMATION_TIMING = {
  railToStaged: DEFAULT_WEB_TIMING.betStaged,
  stagedToRail: DEFAULT_WEB_TIMING.betUnstaged,
  railToKitty: DEFAULT_WEB_TIMING.betConfirmed + 100, // Slightly longer for visual effect
  kittyToRail: DEFAULT_WEB_TIMING.chipFlight + 50,    // Slightly longer for visual effect
  chipFromDealer: DEFAULT_WEB_TIMING.chipFlight + 100,
  insurancePlacement: DEFAULT_WEB_TIMING.insurancePlacement,
  outcomeLabel: DEFAULT_WEB_TIMING.handBoxAppear,
  beamFade: 0.1,  // Alpha change per frame (not duration)
} as const;

// ===========================================
// DEALER POSITIONING
// ===========================================

/** Dealer area position on table */
export const DEALER_POSITION = {
  x: 622,  // Center of table
  y: 180,  // Near top
} as const;

// ===========================================
// SEAT BASE DIMENSIONS
// ===========================================

/** Base seat dimensions before scaling */
export const SEAT_BASE_DIMENSIONS = {
  width: 144,
  height: 168,
} as const;

// ===========================================
// TYPE EXPORTS
// ===========================================

export interface SeatConfig {
  x: number;
  y: number;
  rot: number;
  scale: number;
}

export interface ChipConfig {
  radiusOffset: number;
  x: number;
  y: number;
  scale: number;
}

export interface InsuranceChipConfig {
  x: number;
  y: number;
  scale: number;
}

export interface SeatPosition {
  x: number;
  y: number;
  rotation: number;
}
